import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import firebaseService from "../services/firebase";
import deviceService from "../services/deviceService";
import { useSelector } from "react-redux";
// import { useAuth } from "./AuthContext";

const NotificationContext = createContext({});

export const useNotification = () => useContext(NotificationContext);

export const NotificationProvider = ({ children }) => {
  const [permission, setPermission] = useState("default");
  const [fcmToken, setFcmToken] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isInitialized, setIsInitialized] = useState(false);
  const [deviceStatus, setDeviceStatus] = useState({});
  const user = useSelector((s) => s?.authentication);

  const { isAuthenticated } = user;

  // Initialize Firebase
  const initializeFirebase = useCallback(async () => {
    try {
      const initialized = await firebaseService.initialize();
      if (initialized) {
        setIsInitialized(true);

        // Setup foreground listener
        firebaseService.setupForegroundListener();

        // Listen for messages
        firebaseService.addListener("message", handleNewMessage);
        firebaseService.addListener(
          "permission_granted",
          handlePermissionGranted,
        );
        firebaseService.addListener(
          "permission_denied",
          handlePermissionDenied,
        );

        // Check current state
        const state = firebaseService.getState();
        setPermission(state.permission);
        setFcmToken(state.token);

        console.log("[NOTIFICATION] Firebase initialized");
      }
    } catch (error) {
      console.error("[NOTIFICATION] Initialize failed:", error);
    }
  }, []);

  // Handle new message
  const handleNewMessage = useCallback((payload) => {
    console.log("[NOTIFICATION] New message:", payload);

    // Add to notifications list
    const newNotification = {
      id: Date.now(),
      title: payload.notification?.title || "Notification",
      body: payload.notification?.body || "",
      data: payload.data || {},
      timestamp: new Date(),
      read: false,
      type: payload.data?.type || "general",
    };

    setNotifications((prev) => [newNotification, ...prev.slice(0, 49)]); // Max 50
    setUnreadCount((prev) => prev + 1);

    // Play sound
    if (payload.data?.sound !== "false") {
      firebaseService.playNotificationSound();
    }

    // Show toast notification
    if (payload.data?.show_toast !== "false") {
      // Dispatch event untuk toast component
      window.dispatchEvent(
        new CustomEvent("show-toast", {
          detail: newNotification,
        }),
      );
    }
  }, []);

  // Handle permission granted
  const handlePermissionGranted = useCallback(() => {
    setPermission("granted");
    console.log("[NOTIFICATION] Permission granted");
  }, []);

  // Handle permission denied
  const handlePermissionDenied = useCallback(() => {
    setPermission("denied");
    console.log("[NOTIFICATION] Permission denied");
  }, []);

  // Request permission
  const requestPermission = useCallback(async () => {
    if (!isInitialized) {
      await initializeFirebase();
    }

    const result = await firebaseService.requestPermission();
    setPermission(result);

    if (result === "granted" && user) {
      // Get token dan register device
      await initializeDevice();
    }

    return result;
  }, [isInitialized, user, initializeFirebase]);

  // Initialize device untuk user
  const initializeDevice = useCallback(async () => {
    if (!user || !isAuthenticated) return;

    try {
      const result = await deviceService.initializeDevice(user.id);
      setDeviceStatus(deviceService.getStatus());

      if (result.success) {
        console.log("[NOTIFICATION] Device initialized for user");

        // Update FCM token state
        const token = await firebaseService.getToken();
        setFcmToken(token);
      }

      return result;
    } catch (error) {
      console.error("[NOTIFICATION] Device initialization failed:", error);
      return { success: false, error };
    }
  }, [user, isAuthenticated]);

  // Logout dari device
  const logoutFromDevice = useCallback(async () => {
    try {
      const result = await deviceService.logoutFromDevice();
      setDeviceStatus(deviceService.getStatus());
      setFcmToken(null);

      return result;
    } catch (error) {
      console.error("[NOTIFICATION] Logout failed:", error);
      return { success: false, error };
    }
  }, []);

  // Mark notification as read
  const markAsRead = useCallback((notificationId) => {
    setNotifications((prev) =>
      prev.map((notif) =>
        notif.id === notificationId ? { ...notif, read: true } : notif,
      ),
    );
    setUnreadCount((prev) => Math.max(0, prev - 1));
  }, []);

  // Mark all as read
  const markAllAsRead = useCallback(() => {
    setNotifications((prev) => prev.map((notif) => ({ ...notif, read: true })));
    setUnreadCount(0);
  }, []);

  // Clear all notifications
  const clearAllNotifications = useCallback(() => {
    setNotifications([]);
    setUnreadCount(0);
  }, []);

  // Check device status
  const checkDeviceStatus = useCallback(() => {
    const status = deviceService.getStatus();
    setDeviceStatus(status);
    return status;
  }, []);

  // Effect: Initialize when component mounts
  useEffect(() => {
    initializeFirebase();

    // Load saved notifications dari localStorage
    const savedNotifications = localStorage.getItem("notifications");
    if (savedNotifications) {
      try {
        const parsed = JSON.parse(savedNotifications);
        setNotifications(parsed);

        // Hitung unread
        const unread = parsed.filter((n) => !n.read).length;
        setUnreadCount(unread);
      } catch (error) {
        console.warn("[NOTIFICATION] Failed to load saved notifications");
      }
    }

    // Cleanup
    return () => {
      // Save notifications ke localStorage
      localStorage.setItem("notifications", JSON.stringify(notifications));
    };
  }, [initializeFirebase]);

  // Effect: Initialize device saat user login
  useEffect(() => {
    if (user && isAuthenticated && permission === "granted") {
      initializeDevice();
    }
  }, [user, isAuthenticated, permission, initializeDevice]);

  // Effect: Save notifications ke localStorage
  useEffect(() => {
    if (notifications.length > 0) {
      localStorage.setItem("notifications", JSON.stringify(notifications));
    }
  }, [notifications]);

  const value = {
    // State
    permission,
    fcmToken,
    notifications,
    unreadCount,
    isInitialized,
    deviceStatus,

    // Actions
    requestPermission,
    initializeDevice,
    logoutFromDevice,
    markAsRead,
    markAllAsRead,
    clearAllNotifications,
    checkDeviceStatus,

    // Status checks
    isPermissionGranted: permission === "granted",
    isPermissionDenied: permission === "denied",
    isPermissionDefault: permission === "default",
    hasFcmToken: !!fcmToken,
    isDeviceReady: deviceStatus.isLoggedIn && !!fcmToken,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};
